import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Phone, Hash, Sparkles, StickyNote, Send, Contact as ContactIcon, Plus } from 'lucide-react';
import * as contactsApi from '@/api/contacts.api';
import type { ContactWithRelations } from '@/lib/types';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Spinner, SpinnerOverlay } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { PageHeader } from '@/components/common/PageHeader';
import { NuevoClienteModal } from '@/components/contacts/NuevoClienteModal';
import { dateTime, relativeTime, formatPhoneNumber } from '@/lib/format';
import { getContactDisplayName, getContactSubtitle } from '@/lib/contact-display';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';

interface NoteEntry {
  note: string;
  authorId?: string | null;
  at?: string;
}

/** Claves internas del sistema que NO son "datos del cliente" (no se muestran). */
const INTERNAL_PROFILE_KEYS = new Set(['notes', 'notas_internas']);

/** Etiquetas legibles de las claves conocidas de la tarjeta del lead. */
const PROFILE_KEY_LABELS: Record<string, string> = {
  material_puerta: 'Tipo de puerta',
  zona: 'Zona',
  modelo_interes: 'Modelo de interés',
  precio_modelo: 'Precio',
  presupuesto: 'Presupuesto',
  timing: 'Timing',
  comprobante_fiscal: 'Comprobante fiscal',
  rnc: 'RNC',
  objeciones: 'Objeciones',
  resumen_cierre: 'Resumen de cierre',
  fuente: 'Fuente',
  requiere_humano: 'Requiere humano',
};

function profileKeyLabel(key: string): string {
  return PROFILE_KEY_LABELS[key] ?? key.replace(/_/g, ' ');
}

function ProfileData({ data }: { data: Record<string, unknown> }) {
  // Fuera las claves internas (notas privadas) — esto es la vista de DATOS del cliente.
  const entries = Object.entries(data).filter(([k]) => !INTERNAL_PROFILE_KEYS.has(k));
  if (entries.length === 0) {
    return <p className="text-sm text-slate-400">Sin datos detectados todavía.</p>;
  }
  return (
    <dl className="grid grid-cols-2 gap-3">
      {entries.map(([key, value]) => (
        <div key={key} className="rounded-md bg-surface-muted p-2 dark:bg-slate-800/50">
          <dt className="text-[11px] uppercase tracking-wide text-slate-400">{profileKeyLabel(key)}</dt>
          <dd className="truncate text-sm text-slate-800 dark:text-slate-200">
            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ContactDetail({ contactId }: { contactId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState('');

  const { data: contact, isLoading, isError, refetch } = useQuery({
    queryKey: ['contact', contactId],
    queryFn: () => contactsApi.getContact(contactId),
  });
  const { data: timeline } = useQuery({
    queryKey: ['contact', contactId, 'timeline'],
    queryFn: () => contactsApi.getTimeline(contactId),
  });

  const addNote = useMutation({
    mutationFn: (body: string) => contactsApi.addNote(contactId, body),
    onSuccess: () => {
      setNote('');
      void qc.invalidateQueries({ queryKey: ['contact', contactId] });
      toast.success('Nota agregada');
    },
    onError: () => toast.error('No se pudo agregar la nota'),
  });

  // Error explícito con reintento: nunca un spinner infinito.
  if (isError) {
    return (
      <div className="space-y-3 p-6 text-center">
        <p className="text-sm text-slate-500 dark:text-slate-400">No se pudo cargar el contacto.</p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          Reintentar
        </Button>
      </div>
    );
  }
  if (isLoading || !contact) return <SpinnerOverlay label="Cargando contacto…" />;

  const profile = (contact.profileDataJson ?? {}) as Record<string, unknown>;
  const notes = (Array.isArray(profile.notes) ? profile.notes : []) as NoteEntry[];
  const name = getContactDisplayName(contact);

  return (
    <div className="space-y-4 p-5">
      <div className="flex items-center gap-3">
        <Avatar name={name} size="lg" />
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{name}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-400">
            {contact.phone && (
              <span className="flex items-center gap-1">
                <Phone className="h-3 w-3" /> {formatPhoneNumber(contact.phone)}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Hash className="h-3 w-3" /> {contact.channel?.name ?? contact.channelId}
            </span>
            {contact.blocked && <Badge variant="danger">Bloqueado</Badge>}
          </div>
          {contact.tags && contact.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {contact.tags.map((t) => (
                <Badge key={t.tag.id} variant="brand">
                  {t.tag.name}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Sparkles className="h-4 w-4 text-brand-600" /> Datos detectados por AI
          </CardTitle>
        </CardHeader>
        <CardBody>
          <ProfileData data={profile} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <StickyNote className="h-4 w-4 text-amber-500" /> Notas internas
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex items-end gap-2">
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Agregar una nota interna…"
            />
            <Button
              onClick={() => note.trim() && addNote.mutate(note.trim())}
              loading={addNote.isPending}
              disabled={!note.trim()}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          {notes.length > 0 && (
            <ul className="space-y-2">
              {notes
                .slice()
                .reverse()
                .map((n, i) => (
                  <li key={i} className="rounded-md bg-surface-muted p-3 text-sm dark:bg-slate-800/50">
                    <p className="text-slate-700 dark:text-slate-200">{n.note}</p>
                    {n.at && <p className="mt-1 text-[11px] text-slate-400">{dateTime(n.at)}</p>}
                  </li>
                ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Línea de tiempo</CardTitle>
        </CardHeader>
        <CardBody>
          {!timeline || (timeline.conversations.length === 0 && timeline.events.length === 0) ? (
            <p className="text-sm text-slate-400">Sin actividad registrada.</p>
          ) : (
            <ul className="space-y-2">
              {timeline.conversations.map((conv) => (
                <li key={conv.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 dark:text-slate-200">
                    Conversación · {conv.channel?.name ?? conv.channelId}
                  </span>
                  <span className="text-xs text-slate-400">
                    {relativeTime(conv.lastMessageAt ?? conv.createdAt)}
                  </span>
                </li>
              ))}
              {timeline.events.map((ev) => (
                <li key={ev.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">{ev.type}</span>
                  <span className="text-xs text-slate-400">{relativeTime(ev.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default function Contacts() {
  const [search, setSearch] = useState('');
  // Alta manual (Fase 1 trabajo manual): cliente que compró por fuera.
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['contacts', 'list', search],
    queryFn: () => contactsApi.listContacts({ search: search || undefined, take: 100 }),
  });

  const items: ContactWithRelations[] = data?.items ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="p-6 pb-0">
        <PageHeader
          title="Contactos"
          helpKey="contacts"
          description="Tu CRM de personas que escribieron a tus canales."
          action={
            <Button onClick={() => setNuevoOpen(true)}>
              <Plus className="h-4 w-4" /> Nuevo cliente
            </Button>
          }
        />
        <NuevoClienteModal open={nuevoOpen} onClose={() => setNuevoOpen(false)} />
      </div>
      <div className="flex min-h-0 flex-1 gap-4 p-6">
        <Card className="flex w-full max-w-sm flex-col">
          <div className="border-b border-surface-border p-3 dark:border-slate-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre o teléfono…"
                className="h-9 w-full rounded-md border border-surface-border bg-surface-muted pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-10">
                <Spinner />
              </div>
            ) : items.length === 0 ? (
              <EmptyState
                icon={ContactIcon}
                title="Sin contactos"
                description="Aquí aparecerá cada persona que escriba a su WhatsApp conectado."
                className="m-4"
              />
            ) : (
              <ul className="divide-y divide-surface-border dark:divide-slate-800">
                {items.map((c) => {
                  const name = getContactDisplayName(c);
                  const subtitle = getContactSubtitle(c);
                  return (
                    <li key={c.id}>
                      <button
                        onClick={() => setSelected(c.id)}
                        className={cn(
                          'flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50',
                          selected === c.id && 'bg-brand-50 dark:bg-brand-900/20',
                        )}
                      >
                        <Avatar name={name} seed={c.externalId} size="sm" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{name}</p>
                          <p className="truncate text-xs text-slate-400">{subtitle || '—'}</p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Card>

        <Card className="min-w-0 flex-1 overflow-y-auto">
          {selected ? (
            <ContactDetail contactId={selected} />
          ) : (
            <div className="flex h-full items-center justify-center">
              <EmptyState
                icon={ContactIcon}
                title="Selecciona un contacto"
                description="Elige un contacto para ver su ficha y notas."
              />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
